import React, { useState, useEffect, useCallback } from "react";
import { SettingsCard, SettingsSectionHeader, SettingsSectionTitle, SettingsCountChip, SettingsEmptyState } from "../../Settings/SettingShell";
import { useDocumentsBatch } from "../hooks/useEvidenceLibraryData";
import { getBatchExtractedKpisSummary, retryAnalysis, markVerified, saveBatchKpis } from "../../../_services/documentsService";
import { getExtractedKpis, getDocumentById } from "../../DocSense/services/docSenseService";
import EvidenceTable from "./EvidenceTable";
import EvidenceReviewPanel from "./EvidenceReviewPanel";

/** Indian digit grouping (12,34,567) for every number shown on this page. */
const fmtVal = (v) => {
  if (v == null) return "—";
  const n = parseFloat(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const fmtCount = (n) => (n ?? 0).toLocaleString("en-IN");

const fyLabel = (id, financialYears) => financialYears.find((fy) => String(fy.id) === String(id))?.financial_year_value || "—";

/** Sub-location is the real entity when set; the parent location is only
 *  the aggregating record otherwise. */
const locationLabel = (doc, sources) => {
  const src = sources.find((s) => String(s.id) === String(doc?.sourceId));
  if (!src) return "—";
  const base = src.unitCode ? src.unitCode : `${src.location?.city} - ${src.location?.state}`;
  const sub = (src.subLocation || []).find((s) => String(s.id) === String(doc?.subLocationId));
  return sub ? `${base} / ${sub.subLocation}` : base;
};

/** Cycled by row position purely for visual variety — not tied to KPI identity. */
const KPI_COLORS = [
  { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  { bg: "#fdf4ff", color: "#a21caf", border: "#f5d0fe" },
  { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  { bg: "#fefce8", color: "#a16207", border: "#fef08a" },
  { bg: "#f0fdfa", color: "#0f766e", border: "#99f6e4" },
  { bg: "#fdf2f8", color: "#be185d", border: "#fbcfe8" },
  { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
];

/** Client-side fallback aggregation for groups with no batch-level summary endpoint
 *  (answer-type groups, ungrouped files, or a batch with grouped=false). */
const aggregateKpisClientSide = (perDocResults) => {
  const flat = perDocResults.flatMap(({ doc, kpis }) => kpis.map((k) => ({ ...k, _doc: doc })));
  const buckets = new Map();
  flat.forEach((row) => {
    const key = row.kpiId != null ? `id-${row.kpiId}` : `title-${row.kpiTitle || "unknown"}`;
    if (!buckets.has(key)) buckets.set(key, { kpiId: row.kpiId ?? null, kpiTitle: row.kpiTitle || row.rawTitle || "Untitled KPI", rows: [] });
    buckets.get(key).rows.push(row);
  });
  return [...buckets.values()].map((bucket) => {
    const units = new Set(bucket.rows.map((r) => r.unit ?? r.rawUnit ?? ""));
    const sameUnit = units.size === 1;
    const total = sameUnit ? bucket.rows.reduce((sum, r) => sum + (parseFloat(r.value ?? r.rawValue) || 0), 0) : null;
    return { ...bucket, unit: sameUnit ? [...units][0] : null, mixedUnits: !sameUnit, total, count: bucket.rows.length, documentIds: bucket.rows.map((r) => r._doc.id) };
  }).sort((a, b) => b.count - a.count);
};

/**
 * Full-page evidence/batch detail view — replaces the old GroupKpiSummaryModal
 * + EvidenceGroupDocsModal pair. `target` is either a full group from
 * groupDocuments() (Evidences tab) or `{ type: 'batch', batchId, label }`
 * (Uploaded Documents tab, which hasn't loaded individual documents yet).
 */
const EvidenceDetailPage = ({ target, sources, financialYears, onBack, onViewDocument, onPreview, onDelete, canDelete }) => {
  const isBatch = target.type === "batch";
  const { data: batchDetail, isLoading: batchLoading, refetch: refetchBatch } = useDocumentsBatch(isBatch ? target.batchId : null);
  // Non-batch targets come from a fixed `target.documents` prop with no refetch
  // path back to the parent list — manual "Mark Verified" clicks are applied
  // here optimistically so the page reflects them without a full reload.
  const [verifiedOverrides, setVerifiedOverrides] = useState(new Set());

  const documents = (isBatch ? (batchDetail?.documents || []) : (target.documents || []))
    .map((d) => (verifiedOverrides.has(d.id) ? { ...d, isVerified: true } : d));
  const label = isBatch ? (batchDetail?.name || target.label || `Batch #${target.batchId}`) : target.label;
  const isGroupedBatch = isBatch && batchDetail?.grouped !== false;
  const analyzedDocs = documents.filter((d) => d.docSenseId);
  // Qualitative evidence is never dispatched to DocSense (batches are always
  // quantitative), so there's no analysis status or KPI summary to show.
  const isQualitative = !isBatch && documents[0]?.questionType === "qualitative";

  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiRows, setKpiRows] = useState([]);
  const [statusById, setStatusById] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [justRefreshed, setJustRefreshed] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [rescheduling, setRescheduling] = useState(false);
  // When set, the Documents section is replaced in-place by that document's
  // read-only DocSense analysis (All/Missing/Matched KPIs), per the robot icon.
  const [docSenseDoc, setDocSenseDoc] = useState(null);
  // Extracted KPIs Summary editing: kpiId -> { value, unitId } for rows with
  // unsaved local edits; kpiId currently showing its edit inputs; kpiId currently saving.
  const [editedKpiRows, setEditedKpiRows] = useState({});
  const [editingKpiRowId, setEditingKpiRowId] = useState(null);
  const [savingKpiRowId, setSavingKpiRowId] = useState(null);

  const loadKpiSummary = useCallback(async () => {
    if (isBatch && !batchDetail) return;
    if (!analyzedDocs.length) { setKpiRows([]); return; }
    setKpiLoading(true);
    try {
      if (isGroupedBatch) {
        const rows = await getBatchExtractedKpisSummary(target.batchId);
        setKpiRows(rows.map((r) => ({ ...r, count: r.documentIds?.length ?? 0 })));
      } else {
        const results = await Promise.all(analyzedDocs.map(async (doc) => {
          try { return { doc, kpis: (await getExtractedKpis(doc.docSenseId)) || [] }; }
          catch { return { doc, kpis: [] }; }
        }));
        setKpiRows(aggregateKpisClientSide(results));
      }
    } finally {
      setKpiLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBatch, batchDetail, isGroupedBatch, target.batchId, JSON.stringify(analyzedDocs.map((d) => d.docSenseId))]);

  useEffect(() => { loadKpiSummary(); }, [loadKpiSummary]);

  /** The document that already backs this KPI as a saved answer, if any — carries
   *  the answerId and period saveBatchKpis needs to correct an existing value.
   *  Editing/saving is only offered for grouped batches, where every document in
   *  the batch backs every KPI (per saveBatchKpis' own contract), and only once a
   *  KPI has already been reviewed once (has a period to resend) — brand-new,
   *  never-reviewed KPIs still go through the per-document DocSense Analysis view. */
  const savedDocForKpi = (kpiId) => documents.find((d) => d.questionId === kpiId && d.answerId != null);

  const startEditingKpiRow = (row) => {
    setEditingKpiRowId(row.kpiId);
    setEditedKpiRows((prev) => (prev[row.kpiId] ? prev : { ...prev, [row.kpiId]: { value: row.value ?? row.total, unitId: row.unitId } }));
  };
  const updateEditedKpiRow = (kpiId, patch) => setEditedKpiRows((prev) => ({ ...prev, [kpiId]: { ...prev[kpiId], ...patch } }));
  const resetKpiRow = (kpiId) => setEditedKpiRows((prev) => { const next = { ...prev }; delete next[kpiId]; return next; });
  const resetAllKpiRows = () => { setEditedKpiRows({}); setEditingKpiRowId(null); };

  const saveKpiRow = async (row) => {
    const savedDoc = savedDocForKpi(row.kpiId);
    if (!savedDoc) return;
    const edited = editedKpiRows[row.kpiId] || {};
    const unitId = edited.unitId ?? row.unitId;
    const unit = row.supportedUnits?.find((u) => u.unitId === unitId)?.unit ?? row.unit;
    setSavingKpiRowId(row.kpiId);
    try {
      await saveBatchKpis(target.batchId, [{
        answerId: savedDoc.answerId,
        kpiId: row.kpiId,
        value: edited.value ?? row.value ?? row.total,
        unit,
        unitId,
        fromDate: savedDoc.answerFromDate,
        toDate: savedDoc.answerToDate,
      }], []);
      resetKpiRow(row.kpiId);
      setEditingKpiRowId(null);
      await loadKpiSummary();
      if (isBatch) await refetchBatch();
    } finally {
      setSavingKpiRowId(null);
    }
  };

  // Real DocSense status per document — fixes "shows Analyzed while actually
  // Failed" (docSenseId presence alone doesn't mean the analysis succeeded).
  // Exposed as a callback (not just an effect) so a manual refresh icon and the
  // post-reschedule reload can both pull the latest status on demand.
  const refreshStatuses = useCallback(async () => {
    if (!analyzedDocs.length) { setStatusById({}); return; }
    setStatusLoading(true);
    try {
      const entries = await Promise.all(analyzedDocs.map(async (doc) => {
        try { const r = await getDocumentById(doc.docSenseId); return [doc.docSenseId, r?.status]; }
        catch { return [doc.docSenseId, null]; }
      }));
      setStatusById(Object.fromEntries(entries));
    } finally {
      setStatusLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(analyzedDocs.map((d) => d.docSenseId))]);

  useEffect(() => { refreshStatuses(); }, [refreshStatuses]);

  // Wraps refreshStatuses for the manual refresh button specifically: guarantees
  // the spinner is visible for a minimum stretch (an instant round-trip would
  // otherwise flash too fast to register as "something happened") and then
  // flashes a brief confirmation so the click has an unambiguous outcome.
  const handleManualRefresh = async () => {
    const startedAt = Date.now();
    await refreshStatuses();
    const elapsed = Date.now() - startedAt;
    const minSpinMs = 500;
    if (elapsed < minSpinMs) await new Promise((resolve) => setTimeout(resolve, minSpinMs - elapsed));
    setJustRefreshed(true);
    setTimeout(() => setJustRefreshed(false), 1500);
  };

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Any already-analyzed document is retryable for now (see EvidenceTable's isRetryable).
  const retryableIds = analyzedDocs.map((d) => d.id);
  const allSelected = retryableIds.length > 0 && retryableIds.every((id) => selectedIds.has(id));
  const handleToggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(retryableIds));

  const handleReschedule = async () => {
    if (!selectedIds.size || rescheduling) return;
    setRescheduling(true);
    try {
      const result = await retryAnalysis([...selectedIds]);
      setSelectedIds(new Set());
      await refreshStatuses();
      return result;
    } finally {
      setRescheduling(false);
    }
  };

  const handleMarkVerified = async (doc) => {
    await markVerified([doc.id]);
    setVerifiedOverrides((prev) => new Set(prev).add(doc.id));
    if (isBatch) await refetchBatch();
  };

  const handleOpenDocSense = (doc) => setDocSenseDoc(doc);
  const handleCloseDocSense = () => setDocSenseDoc(null);

  const statusCounts = analyzedDocs.reduce((acc, d) => {
    const s = statusById[d.docSenseId];
    if (s === "COMPLETED") acc.completed++;
    else if (s === "FAILED" || s === "TIMED_OUT") acc.failed++;
    else if (s === "PROCESSING" || s === "PENDING") acc.processing++;
    return acc;
  }, { completed: 0, failed: 0, processing: 0 });
  // Counted off every document, not just analyzedDocs — a document saved with
  // shouldParse:false is verified immediately without ever reaching DocSense,
  // so it would otherwise never show up here at all.
  const verifiedCount = documents.filter((d) => d.isVerified).length;

  const loading = (isBatch && batchLoading) || kpiLoading;

  return (
    <div>
      <button
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", color: "#3f88a5", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 16 }}
      >
        <i className="fas fa-arrow-left" /> Back
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "#f0f9ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <i className="fas fa-file-alt" style={{ color: "#3f88a5", fontSize: 16 }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>{fmtCount(documents.length)} file{documents.length === 1 ? "" : "s"}</div>
        </div>
        {/* Verification is the single most important thing to know at a glance —
            shown right beside the title, not buried further down the page. */}
        {!isQualitative && documents.length > 0 && (
          <span style={{
            display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 800, padding: "8px 16px", borderRadius: 10,
            background: verifiedCount === documents.length ? "#dcfce7" : verifiedCount > 0 ? "#fef9c3" : "#fff7ed",
            color: verifiedCount === documents.length ? "#166534" : verifiedCount > 0 ? "#854d0e" : "#c2410c",
          }}>
            <i className={`fas ${verifiedCount === documents.length ? "fa-check-circle" : verifiedCount > 0 ? "fa-adjust" : "fa-exclamation-circle"}`} style={{ fontSize: 16 }} />
            {verifiedCount === documents.length
              ? "All Verified"
              : verifiedCount > 0
                ? `${fmtCount(verifiedCount)}/${fmtCount(documents.length)} Verified`
                : "Not Verified"}
          </span>
        )}
      </div>

      {/* Shared across every document in this group/batch — shown once here
          instead of repeated on every row of the Documents table below. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, padding: "5px 11px", borderRadius: 20, background: "#eff6ff", color: "#2563eb" }}>
          <i className="fas fa-calendar-alt" style={{ marginRight: 6 }} />{fyLabel(documents[0]?.financialYearId, financialYears)}
        </span>
        {!isQualitative && (
          <span style={{ fontSize: 11.5, fontWeight: 700, padding: "5px 11px", borderRadius: 20, background: "#f0fdfa", color: "#0f766e" }}>
            <i className="fas fa-map-marker-alt" style={{ marginRight: 6 }} />{locationLabel(documents[0], sources)}
          </span>
        )}
        <span style={{ fontSize: 11.5, fontWeight: 700, padding: "5px 11px", borderRadius: 20, background: "#fdf4ff", color: "#a21caf" }}>
          <i className={`fas ${isQualitative ? "fa-align-left" : "fa-chart-bar"}`} style={{ marginRight: 6 }} />{isQualitative ? "Qualitative" : "Quantitative"}
        </span>
      </div>

      {!isQualitative && analyzedDocs.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "#dcfce7", color: "#166534" }}>{fmtCount(statusCounts.completed)} completed</span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "#fee2e2", color: "#991b1b" }}>{fmtCount(statusCounts.failed)} failed</span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "#e0f2fe", color: "#0369a1" }}>{fmtCount(statusCounts.processing)} processing</span>
          <button
            onClick={handleManualRefresh}
            disabled={statusLoading}
            aria-label="Refresh analysis status"
            title="Refresh analysis status"
            style={{
              display: "flex", alignItems: "center", gap: 6, justifyContent: "center", padding: "0 10px", height: 26, borderRadius: 13,
              border: `1px solid ${justRefreshed ? "#bbf7d0" : "#e2e8f0"}`,
              background: justRefreshed ? "#dcfce7" : "#fff",
              color: justRefreshed ? "#166534" : "#3f88a5",
              cursor: statusLoading ? "default" : "pointer", fontSize: 11, fontWeight: 700, transition: "background .15s, color .15s, border-color .15s",
            }}
          >
            <i className={`fas ${statusLoading ? "fa-sync-alt fa-spin" : justRefreshed ? "fa-check" : "fa-sync-alt"}`} style={{ fontSize: 11 }} />
            {justRefreshed && !statusLoading ? "Refreshed" : null}
          </button>
        </div>
      )}

      {!isQualitative && (
        <SettingsCard style={{ marginBottom: 20 }}>
          <SettingsSectionHeader>
            <i className="fas fa-chart-bar" style={{ fontSize: 12, color: "#3f88a5" }} />
            <SettingsSectionTitle>Extracted KPIs Summary</SettingsSectionTitle>
            <SettingsCountChip>{kpiRows.length}</SettingsCountChip>
            {Object.keys(editedKpiRows).length > 0 && (
              <button
                onClick={resetAllKpiRows}
                style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
              >
                <i className="fas fa-undo" />
                Reset All ({Object.keys(editedKpiRows).length})
              </button>
            )}
          </SettingsSectionHeader>
          <div style={{ padding: 16 }}>
            {loading ? (
              <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
            ) : !analyzedDocs.length ? (
              <SettingsEmptyState>
                <i className="fas fa-robot s-empty-icon" />
                <div className="s-empty-title">Not analyzed yet</div>
                <div className="s-empty-sub">None of these documents have been processed by DocSense.</div>
              </SettingsEmptyState>
            ) : kpiRows.length === 0 ? (
              <SettingsEmptyState>
                <i className="fas fa-search s-empty-icon" />
                <div className="s-empty-title">No KPIs extracted</div>
              </SettingsEmptyState>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {kpiRows.map((row, i) => {
                  const key = row.kpiId ?? row.kpiTitle;
                  const palette = KPI_COLORS[i % KPI_COLORS.length];
                  const savedDoc = row.kpiId != null ? savedDocForKpi(row.kpiId) : null;
                  const isSaved = !!savedDoc;
                  const edited = editedKpiRows[row.kpiId];
                  const isDirty = !!edited && (edited.value !== (row.value ?? row.total) || edited.unitId !== row.unitId);
                  const isEditingRow = editingKpiRowId === row.kpiId;
                  const isSavingRow = savingKpiRowId === row.kpiId;
                  const canEditRow = isGroupedBatch && row.kpiId != null && !row.mixedUnits;

                  return (
                    <div
                      key={key}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: palette.bg, border: `1px solid ${isDirty ? "#f0a500" : palette.border}` }}
                    >
                      <div style={{ width: 34, height: 34, borderRadius: 9, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <i className="fas fa-chart-line" style={{ color: palette.color, fontSize: 14 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{row.kpiTitle}</span>
                          {isDirty ? (
                            <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#fef9c3", color: "#854d0e" }}>Unsaved changes</span>
                          ) : isSaved ? (
                            <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#dcfce7", color: "#166534" }}>Saved</span>
                          ) : (
                            <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#f1f5f9", color: "#64748b" }}>Not saved</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: palette.color, fontWeight: 600 }}>{fmtCount(row.count)} document{row.count === 1 ? "" : "s"}</div>
                      </div>

                      {isEditingRow ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            type="number"
                            value={edited?.value ?? ""}
                            onChange={(e) => updateEditedKpiRow(row.kpiId, { value: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                            style={{ width: 110, padding: "6px 8px", borderRadius: 7, border: "1px solid #e2e8f0", fontSize: 13, fontWeight: 700 }}
                          />
                          {row.supportedUnits?.length > 0 && (
                            <select
                              value={edited?.unitId ?? ""}
                              onChange={(e) => updateEditedKpiRow(row.kpiId, { unitId: Number(e.target.value) })}
                              style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid #e2e8f0", fontSize: 12.5 }}
                            >
                              {row.supportedUnits.map((u) => <option key={u.unitId} value={u.unitId}>{u.unit}</option>)}
                            </select>
                          )}
                          <button
                            onClick={() => saveKpiRow(row)}
                            disabled={isSavingRow}
                            title={isSaved ? "Save correction" : "Review via DocSense Analysis to set a period first"}
                            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "none", background: "#16a34a", color: "#fff", fontSize: 12, fontWeight: 700, cursor: isSavingRow ? "default" : "pointer" }}
                          >
                            <i className={`fas ${isSavingRow ? "fa-spinner fa-spin" : "fa-check"}`} /> Save
                          </button>
                          <button
                            onClick={() => { resetKpiRow(row.kpiId); setEditingKpiRowId(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <div style={{ textAlign: "right" }}>
                            {row.mixedUnits ? (
                              <span style={{ fontSize: 11.5, fontWeight: 700, color: palette.color }}>Mixed units</span>
                            ) : (
                              <span style={{ fontSize: 15, fontWeight: 800, color: palette.color }}>
                                {fmtVal(edited?.value ?? row.value ?? row.total)} <span style={{ fontSize: 11, fontWeight: 600 }}>{row.unit}</span>
                              </span>
                            )}
                          </div>
                          {canEditRow && (
                            <button
                              onClick={() => startEditingKpiRow(row)}
                              title={isSaved ? "Correct this value" : "Editing requires a previously-saved period — use DocSense Analysis to review and save this KPI first"}
                              disabled={!isSaved}
                              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: "1px solid #fff", background: "#fff", color: isSaved ? palette.color : "#cbd5e1", cursor: isSaved ? "pointer" : "not-allowed", flexShrink: 0 }}
                            >
                              <i className="fas fa-pen" style={{ fontSize: 12 }} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SettingsCard>
      )}

      <SettingsCard>
        <SettingsSectionHeader>
          <i className={`fas ${docSenseDoc ? "fa-robot" : "fa-file-alt"}`} style={{ fontSize: 12, color: "#3f88a5" }} />
          <SettingsSectionTitle>{docSenseDoc ? `DocSense Analysis — ${docSenseDoc.fileName}` : "Documents"}</SettingsSectionTitle>
          {!docSenseDoc && <SettingsCountChip>{documents.length}</SettingsCountChip>}
          {!docSenseDoc && !isQualitative && selectedIds.size > 0 && (
            <button
              onClick={handleReschedule}
              disabled={rescheduling}
              style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "none", background: "#3f88a5", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: rescheduling ? "default" : "pointer" }}
            >
              <i className={`fas ${rescheduling ? "fa-spinner fa-spin" : "fa-redo"}`} />
              Reschedule ({selectedIds.size})
            </button>
          )}
        </SettingsSectionHeader>
        <div style={{ padding: 16 }}>
          {docSenseDoc ? (
            <EvidenceReviewPanel docSenseId={docSenseDoc.docSenseId} readOnly onBack={handleCloseDocSense} />
          ) : isBatch && batchLoading ? (
            <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
          ) : (
            <EvidenceTable
              documents={documents}
              statusById={statusById}
              selectable={!isQualitative}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              allSelected={allSelected}
              onToggleSelectAll={handleToggleSelectAll}
              onView={onViewDocument}
              onPreview={onPreview}
              onReview={handleOpenDocSense}
              onMarkVerified={handleMarkVerified}
              onDelete={(doc) => onDelete(doc, refetchBatch)}
              canDelete={canDelete}
            />
          )}
        </div>
      </SettingsCard>
    </div>
  );
};

export default EvidenceDetailPage;
